import React, { useState } from 'react';
import { generateAttributeList, generateAuditReportWithProgress } from '../services/backendExcelService';

interface YamlEditorProps {
  yamlCode: string;
  yamlLoading: boolean;
  yamlError: string;
  selectedRuleset: string;
  selectedRulesetKey: string;
  token: string;
  guidelineReportLoading: boolean;
  guidelineReportProgress: { current: number; total: number } | null;
  guidelineReportProgressMessage: string;
  setYamlError: (error: string) => void;
  setGuidelineReportLoading: (loading: boolean) => void;
  setGuidelineReportProgress: (progress: { current: number; total: number } | null) => void;
  setGuidelineReportProgressMessage: (message: string) => void;
}

const YamlEditor: React.FC<YamlEditorProps> = ({
  yamlCode,
  yamlLoading,
  yamlError,
  selectedRuleset,
  selectedRulesetKey,
  token,
  guidelineReportLoading,
  guidelineReportProgress,
  guidelineReportProgressMessage,
  setYamlError,
  setGuidelineReportLoading,
  setGuidelineReportProgress,
  setGuidelineReportProgressMessage
}) => {
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const handleGenerateAttributeList = async () => {
    if (!selectedRulesetKey) {
      setYamlError('No ruleset selected to generate attribute list');
      return;
    }

    try {
      await generateAttributeList(selectedRulesetKey);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate attribute list';
      setYamlError(errorMessage);
      console.error('Error generating attribute list:', error);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    
    if (files.length !== pdfFiles.length) {
      setYamlError('Only PDF files are supported');
      return;
    }
    
    setAttachments(prev => [...prev, ...pdfFiles]);
    setYamlError('');
  };

  const handleRemoveFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerateGuidelineReport = async () => {
    if (!selectedRulesetKey) {
      setYamlError('No ruleset selected to generate audit report');
      return;
    }

    setGuidelineReportLoading(true);
    setYamlError('');
    setGuidelineReportProgress(null);

    try {
      await generateAuditReportWithProgress(
        selectedRulesetKey,
        (progressMessage: string) => {
          // Update progress display
          console.log('🔄 onProgress called with:', progressMessage);
          setGuidelineReportProgressMessage(progressMessage);
          console.log('🔄 State should now be:', progressMessage);
        },
        attachments
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate audit report';
      setYamlError(errorMessage);
      console.error('Error generating audit report:', error);
    } finally {
      setGuidelineReportLoading(false);
      setGuidelineReportProgress(null);
      setGuidelineReportProgressMessage('');
    }
  };

  const handleCopyToClipboard = () => {
    if (yamlCode) {
      navigator.clipboard.writeText(yamlCode);
    }
  };

  return (
    <div className="yaml-editor">
      <div className="editor-header">
        <h3 className="editor-title">YAML Code</h3>
        <div className="editor-actions">
          <button 
            className="editor-action"
            onClick={handleCopyToClipboard}
            title="Copy to clipboard"
          >
            Copy
          </button>
          {yamlCode && (
            <button 
              className="editor-action"
              onClick={handleGenerateAttributeList}
              title="Download attribute list as Excel"
            >
              Download Attribute List
            </button>
          )}
          {yamlCode && (
            <button 
              className="editor-action"
              onClick={handleGenerateGuidelineReport}
              disabled={guidelineReportLoading}
              title="Download guideline audit report as Excel"
            >
              {(() => {
                const buttonText = guidelineReportLoading 
                  ? guidelineReportProgressMessage || 'Starting...'
                  : 'Download Guideline Audit Report';
                console.log('🔘 Button rendering with text:', buttonText, 'loading:', guidelineReportLoading, 'message:', guidelineReportProgressMessage);
                return buttonText;
              })()}
            </button>
          )}
        </div>
      </div>
      
      {/* File Upload Section */}
      <div className="file-upload-section">
        <div className="file-upload-header">
          <button
            type="button"
            className="file-upload-toggle"
            onClick={() => setShowFileUpload(!showFileUpload)}
          >
            {showFileUpload ? '▼' : '▶'} PDF Attachments used as reference for Audit Report (Optional)
          </button>
        </div>
        
        {showFileUpload && (
          <div className="file-upload-content">
            <div className="file-upload-area">
              <input
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileUpload}
                className="file-input"
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload" className="file-upload-label">
                📎 Choose PDF files to attach to the audit report
              </label>
            </div>
            
            {attachments.length > 0 && (
              <div className="attachments-list">
                <h4>Attached Files ({attachments.length}):</h4>
                {attachments.map((file, index) => (
                  <div key={index} className="attachment-item">
                    <span className="attachment-name">📄 {file.name}</span>
                    <button
                      type="button"
                      className="remove-attachment"
                      onClick={() => handleRemoveFile(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="editor-content">
        {yamlLoading && (
          <div className="yaml-loading">
            Loading YAML code...
          </div>
        )}
        {yamlError && (
          <div className="yaml-error">
            {yamlError}
          </div>
        )}
        {yamlCode && (
          <textarea
            className="yaml-code"
            value={yamlCode}
            readOnly
            placeholder="YAML code will appear here..."
          />
        )}
      </div>
    </div>
  );
};

export default YamlEditor; 